import {
  GetConfigurationDTO,
  GetEmissionsConsumptionDTO,
  GetTyreInformationDTO,
  VWGConfigurationService,
} from '@tribal-worldwide-london/fa-configuration-service-vwg';
import ContentManager from '@tribal-worldwide-london/fa-content';
import { GlobalStoreStateConfiguration } from '@/stores/globalStore';
import { handleAllErrors } from '../utils/response';
import { reduceDataByKey } from '../utils/object';

export type ConfigurationLoaderFunctionArgs = {
  params: {
    configurations: GlobalStoreStateConfiguration[];
  };
};

export type ConfigurationLoaderServiceArgs = {
  configurationService: VWGConfigurationService;
};

const configurationLoader = async (
  { configurationService }: ConfigurationLoaderServiceArgs,
  { params }: ConfigurationLoaderFunctionArgs,
  contentManager: Pick<ContentManager, 'get'>
) => {
  const { configurations } = params;
  const configurationRequests: Promise<any>[] = [];
  const tyreInformationRequests: Promise<any>[] = [];
  const emissionsConsumptionRequests: Promise<any>[] = [];
  const marketingShortCodes: string[] = [];

  configurations.forEach((app) => {
    const { marketingShortCode, pdbData } = app;

    if (marketingShortCode && pdbData?.derivative && pdbData?.capi) {
      marketingShortCodes.push(marketingShortCode);

      const {
        derivative,
        capi: {
          configuration: { exterior, interior, extras },
        },
      } = pdbData;

      const options = encodeURIComponent(
        [exterior, interior, extras.join('-')].join(',')
      );

      configurationRequests.push(
        configurationService.getConfiguration(
          new GetConfigurationDTO(
            derivative.modelId,
            derivative.salesGroupId,
            derivative.id,
            derivative.version.toString(),
            options
          )
        )
      );

      tyreInformationRequests.push(
        configurationService.getTyreInformation(
          new GetTyreInformationDTO(
            derivative.modelId,
            derivative.salesGroupId,
            derivative.id,
            derivative.version.toString(),
            options,
            true
          )
        )
      );

      emissionsConsumptionRequests.push(
        configurationService.getEmissionsConsumption(
          new GetEmissionsConsumptionDTO(
            derivative.modelId,
            derivative.salesGroupId,
            derivative.id,
            derivative.version.toString(),
            options
          )
        )
      );
    }
  });

  const configurationResponses = await Promise.all([
    Promise.all(configurationRequests),
    Promise.all(tyreInformationRequests),
    Promise.all(emissionsConsumptionRequests),
  ]);

const allResponses = configurationResponses.flat();
  handleAllErrors(allResponses, contentManager, true);

  const configurationResponsesByMarketingShortCode = reduceDataByKey(
    marketingShortCodes,
    (key: string, i: number) => ({
      [key]: {
        ...configurationResponses[0][i].data,
        tyreInformationData: configurationResponses[1][i].data,
        emissionsConsumptionData: configurationResponses[2][i].data,
      },
    })
  );

  return Promise.resolve(configurationResponsesByMarketingShortCode);
};

export default configurationLoader;
